'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/transitional/components/Select';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { isComparisonConfig } from 'reporting-data/compare';
import { getUseFiscalYear, isFiscalYearSupportedRangeType } from 'reporting-data/lib/fiscalYearUtils';
import { isMismatchedDimensionDateFilterPropertyTimeSeries } from 'reporting-data/properties/isMismatchedDimensionDateFilterPropertyTimeSeries';
import { getDimensionFromConfig, getReportConfig } from 'reporting-data/report/configReportGetters';
import { getCustomWidgetType, isTableReport, getDataType } from 'reporting-data/report/reportGetters';
import { CUSTOM_WIDGET_TYPES } from 'reporting-data/tsTypes/customWidgetTypes';
import { isMultiConfigReport } from 'reporting-data/tsTypes/reportTypes';
import { DateActionTypes } from 'reporting-ui-components/constants/trackingConstants';
import { getCompareOptions } from 'reporting-ui-components/lib/compare-options';
import { EVENTS } from 'reporting-ui-components/lib/events';
import useCloneTracker from 'reporting-ui-components/lib/useCloneTracker';
import { ReportType } from 'reporting-ui-components/types/ReportTypes';
import { shouldDisableCompareOption } from '../reportEditors/common/comparisonHelpers';
import { ReportOptionsCategories } from '../singleReportViewer/fullPage/sidebar/categorized/ReportOptionCategory';
import BaseSelectEditorComponent from './common/BaseSelectEditorComponent';
import { SRV_REPORT_COMPARISON_DROPDOWN_GATE } from 'reporting-enablement/constants/gates';
import { SRVReportComparisonDropdown } from '../singleReportViewer/fullPage/sidebar/report-options-components/SRVReportComparisonDropdown';
const NO_COMPARE = 'NO_COMPARE';
const EXT_LINE_ITEMS_JOINED_DEALS = 'EXT_LINE_ITEMS_JOINED_DEALS';
import { useGate } from 'reporting-enablement/hooks/useGate';
import { jsx as _jsx } from "react/jsx-runtime";
export const getTooltipText = report => {
  const isInvalidDateRangeAndDimension = isMismatchedDimensionDateFilterPropertyTimeSeries(report);
  if (isInvalidDateRangeAndDimension) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-ui-components.singleReportViewer.comparisonTooltip.mismatchProperties"
    });
  }
  const dimensions = getDimensionFromConfig(getReportConfig(report));
  const isMultiDimensional = dimensions && dimensions.size > 1;
  if (isMultiDimensional) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-ui-components.singleReportViewer.comparisonTooltip.multiDimensional"
    });
  }
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "reporting-ui-components.singleReportViewer.comparisonTooltip.unavailable"
  });
};
const CompareEditor = ({
  report,
  onChange,
  appUsageTracker,
  trackingAssetType,
  trackingApplicationName,
  trackingScreenName,
  isV2FilterExperience
}) => {
  const isUngatedForSRVReportComparisonDropdown = useGate(SRV_REPORT_COMPARISON_DROPDOWN_GATE);
  const usageTracker = useCloneTracker(appUsageTracker);
  const config = report.get('config');
  const dateRange = report.getIn(['config', 'filters', 'dateRange', 'value']);
  const rangeType = dateRange.get('rangeType');
  const isInvalidDateRangeAndDimension = isMismatchedDimensionDateFilterPropertyTimeSeries(report);
  const shouldDisableEditor = shouldDisableCompareOption(report);
  const compare = isComparisonConfig(config) ? config.get('compare') : NO_COMPARE;
  const useFiscalYear = getUseFiscalYear(config) && isFiscalYearSupportedRangeType(rangeType);
  const options = getCompareOptions({
    dateRange,
    compare,
    explicit: false,
    useFiscalYear
  });
  if (isComparisonConfig(config) && isInvalidDateRangeAndDimension) {
    onChange(report.setIn(['config', 'compare'], null));
  }
  const compareOptions = useMemo(() => {
    return [{
      value: NO_COMPARE,
      text: I18n.text('report.customSeries.noComparison')
    }, ...options.toJS()];
  }, [options]);
  const handleOnChange = useCallback(({
    target: {
      value: newCompare
    }
  }) => {
    usageTracker.track(EVENTS.changeSrvDateFilters, {
      action: DateActionTypes.COMPARED_TO,
      assetType: trackingAssetType || 'report',
      applicationName: trackingApplicationName,
      whereScreen: trackingScreenName,
      isNewFilterExperience: isV2FilterExperience
    });
    const dateRangeUseFiscalYear = getUseFiscalYear(report.get('config'));
    onChange(report.setIn(['config', 'compare'], newCompare === NO_COMPARE ? null : newCompare).setIn(['config', 'useFiscalYearInAggregation'], dateRangeUseFiscalYear));
  }, [isV2FilterExperience, onChange, report, trackingApplicationName, trackingAssetType, trackingScreenName, usageTracker]);
  const label = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "settingLabels.compare.label"
  });
  if (isV2FilterExperience) {
    return /*#__PURE__*/_jsx(BaseSelectEditorComponent, {
      tooltipHeader: label,
      disabled: shouldDisableEditor,
      onChange: handleOnChange,
      options: compareOptions,
      tooltipText: shouldDisableEditor ? getTooltipText(report) : null,
      value: compare
    });
  }
  if (isUngatedForSRVReportComparisonDropdown) {
    return /*#__PURE__*/_jsx(SRVReportComparisonDropdown, {
      report: report,
      onChange: onChange,
      trackingAssetType: trackingAssetType,
      trackingApplicationName: trackingApplicationName,
      trackingScreenName: trackingScreenName,
      isV2FilterExperience: isV2FilterExperience
    });
  }
  return /*#__PURE__*/_jsx(FormControl, {
    label: label,
    children: /*#__PURE__*/_jsx(Tooltip, {
      disabled: !shouldDisableEditor,
      title: getTooltipText(report),
      children: /*#__PURE__*/_jsx(Select, {
        disabled: shouldDisableEditor,
        value: compare,
        options: compareOptions,
        onChange: handleOnChange
      })
    })
  });
};
CompareEditor.isUsableForReport = report => {
  // Once these reports are migrated to be on platform, we can remove this check
  if ([CUSTOM_WIDGET_TYPES.PIPELINE_SNAPSHOTS, CUSTOM_WIDGET_TYPES.SALES_QUOTA, CUSTOM_WIDGET_TYPES.ENGAGEMENT_STREAM, CUSTOM_WIDGET_TYPES.WATERFALL, CUSTOM_WIDGET_TYPES.CHANGE_HISTORY_DEAL_LIST, CUSTOM_WIDGET_TYPES.COMPLETED_ACTIVITIES, CUSTOM_WIDGET_TYPES.DEAL_PUSH_ANALYSIS].includes(getCustomWidgetType(report))) {
    return false;
  }
  const dataType = getDataType(report);
  if (dataType === EXT_LINE_ITEMS_JOINED_DEALS) {
    return false;
  }
  if (isMultiConfigReport(report) || isTableReport(report)) {
    // MultiConfig reports have custom visualization.The compare dataset is not accounted
    // for in the custom visualization.
    return false;
  }
  return report.hasIn(['config', 'filters', 'dateRange', 'value']);
};
CompareEditor.isDateEditor = true;
CompareEditor.category = ReportOptionsCategories.Compare;
CompareEditor.propTypes = {
  appUsageTracker: PropTypes.object,
  report: ReportType.isRequired,
  onChange: PropTypes.func.isRequired,
  trackingAssetType: PropTypes.string,
  trackingApplicationName: PropTypes.string,
  trackingScreenName: PropTypes.string,
  isV2FilterExperience: PropTypes.bool
};
export default CompareEditor;